// The strip above the site header (templates/includes/site_banner.html). It stays in the page flow and scrolls
// away; the fixed header follows its visible edge through --site-banner-offset, and the header of the ads and bots
// pages, which scrolls with the page, starts under it through --site-banner-height (src/common/css/index.scss).
const DISMISSED_COOKIE = 'site_banner_dismissed';
const DISMISSED_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;
const DISMISSED_KEEP = 20;
const IN_VIEW_CLASS = 'site-banner-in-view';
const HOVER_HOLD_CLASS = 'site-banner-hover-hold';
// A real move; the browser's synthetic move after a layout change or a scroll repeats the last spot.
const HOVER_HOLD_RELEASE_PX = 3;

function setOffsets(height) {
    const root = document.documentElement;
    const offset = Math.max(0, height - window.scrollY);
    root.style.setProperty('--site-banner-height', `${height}px`);
    root.style.setProperty('--site-banner-offset', `${offset}px`);
    root.classList.toggle(IN_VIEW_CLASS, offset > 0);
}

function clearOffsets() {
    const root = document.documentElement;
    root.style.removeProperty('--site-banner-height');
    root.style.removeProperty('--site-banner-offset');
    root.classList.remove(IN_VIEW_CLASS);
}

function dismissedTokens() {
    const entry = document.cookie.split('; ').find((part) => part.startsWith(`${DISMISSED_COOKIE}=`));
    return entry ? entry.slice(DISMISSED_COOKIE.length + 1).split('|').filter(Boolean) : [];
}

// The server reads the same cookie (notifications/banners.py) and stops rendering the banner.
function rememberDismissed(token) {
    const tokens = dismissedTokens().filter((item) => item !== token);
    tokens.push(token);
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${DISMISSED_COOKIE}=${tokens.slice(-DISMISSED_KEEP).join('|')}; `
        + `Max-Age=${DISMISSED_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

// The header moves up under the mouse that closed the banner, where the next hover update (a scroll, for one) would
// open its hover menus. It ignores the pointer (src/common/css/_site-banner.scss) until the pointer moves or presses.
function holdHeaderHover(x, y) {
    const root = document.documentElement;
    const release = () => {
        root.classList.remove(HOVER_HOLD_CLASS);
        window.removeEventListener('pointermove', releaseOnMove, true);
        window.removeEventListener('pointerdown', release, true);
    };
    const releaseOnMove = (event) => {
        if (Math.hypot(event.clientX - x, event.clientY - y) >= HOVER_HOLD_RELEASE_PX) {
            release();
        }
    };
    root.classList.add(HOVER_HOLD_CLASS);
    window.addEventListener('pointermove', releaseOnMove, true);
    window.addEventListener('pointerdown', release, true);
}

// The close button disappears with the banner. The focus goes to the header's first control (the logo link), or on
// the login pages, which have no header, to the page's first link or button.
function focusAfterDismissal() {
    const target = document.querySelector('.app-header a[href], .app-header button')
        || document.querySelector('a[href], button:not([disabled])');
    if (target) {
        target.focus({preventScroll: true});
    }
}

export function initSiteBanner() {
    const banner = document.querySelector('[data-site-banner]');
    if (!banner) {
        clearOffsets();
        return;
    }
    const update = () => setOffsets(banner.offsetHeight);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    update();
    window.addEventListener('scroll', update, {passive: true});
    window.addEventListener('resize', update);
    if (observer) {
        observer.observe(banner);
    }
    const closeButton = banner.querySelector('[data-site-banner-close]');
    if (!closeButton) {
        return;
    }
    // Only a mouse gets the hover hold: a keyboard press has no pointerdown (and a click detail of 0), and a later
    // tap or pen press on the header must reach it without a move first.
    let pressedWith = '';
    closeButton.addEventListener('pointerdown', (event) => {
        pressedWith = event.pointerType;
    });
    closeButton.addEventListener('click', (event) => {
        const byMouse = pressedWith === 'mouse' && event.detail > 0;
        pressedWith = '';
        rememberDismissed(banner.dataset.siteBannerToken);
        window.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
        if (observer) {
            observer.disconnect();
        }
        if (byMouse) {
            holdHeaderHover(event.clientX, event.clientY);
        }
        banner.remove();
        clearOffsets();
        focusAfterDismissal();
    });
}
